/* ============================================================================
 * ALTIORA — Page publique /securite
 * ----------------------------------------------------------------------------
 * Positionnement : argument commercial concret pour un premier partenariat
 * institutionnel (ministère, université, CIUSSS). Reflète la doctrine ratifiée
 * (Principe 6 — Explicabilité totale), les fixes de sécurité livrés (SEC-001
 * à SEC-003), et les garanties d'anonymat (M7 · k ≥ 20).
 *
 * Règle des 5 Valeurs :
 *  - Membre : rassure sur le sérieux des garanties de vie privée
 *  - Pro : peut renvoyer ses clients institutionnels vers cette page
 *  - Institution : prérequis pour un premier appel commercial
 *  - Science ALTIORA : ancre la doctrine dans un discours public
 *  - Modèle éco : matériel de vente réutilisable
 * ==========================================================================*/
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Lock, Radar, Eye, KeyRound, RefreshCw, FileText,
  Users, GraduationCap, Building2, ArrowRight, CheckCircle2, Info,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const PILLARS = [
  {
    icon: KeyRound,
    title_fr: "Révocation instantanée",
    body_fr:
      "Chaque JWT porte un numéro de version (tv). Un reset de mot de passe ou une suspension incrémente cette version — tous les tokens antérieurs deviennent invalides en temps réel, sans attendre l'expiration.",
    proof_fr: "Correctif SEC-001 · testé automatisé",
  },
  {
    icon: Lock,
    title_fr: "K-anonymité stricte · k ≥ 20",
    body_fr:
      "Les cohortes agrégées (moteur M7 — intelligence territoriale) ne dévoilent aucune donnée si la population filtrée est inférieure à 20 individus. Un partenaire institutionnel voit des tendances, jamais des personnes.",
    proof_fr: "Doctrine §5 M7 · Trusted Intelligence™",
  },
  {
    icon: Eye,
    title_fr: "Trusted Intelligence™",
    body_fr:
      "Chaque score, chaque recommandation, chaque mise en relation expose : pourquoi cette conclusion, quelles données précises l'ont produite, quel niveau de confiance, quelles actions peuvent la modifier. Aucune boîte noire.",
    proof_fr: "Principe 6 · ratifié 2026-02-08",
  },
  {
    icon: RefreshCw,
    title_fr: "Limitation d'usage",
    body_fr:
      "Les endpoints coûteux (analyse IA, export PDF, agrégations territoriales) sont protégés par des quotas per-utilisateur persistants. Aucun compte, même compromis, ne peut drainer le budget d'intelligence artificielle.",
    proof_fr: "Correctif SEC-003 · 429 déclenché automatiquement",
  },
  {
    icon: ShieldCheck,
    title_fr: "Secrets isolés",
    body_fr:
      "Aucune variable d'environnement (JWT_SECRET, clés Stripe, LLM key, tokens partenaires) n'est versionnée. Les .env sont exclus par convention immuable du dépôt.",
    proof_fr: "Correctif SEC-002 · vérifié par git check-ignore",
  },
  {
    icon: FileText,
    title_fr: "Explicabilité auditable",
    body_fr:
      "Chaque calcul IMES™ v2 s'appuie sur une formule publiée, versionnée (SemVer), et documentée dans la doctrine ALTIORA Méthode™ — reproductible par un tiers, contestable par le membre.",
    proof_fr: "Doctrine ratifiée · Livre blanc en préparation",
  },
];

const AUDIENCES = [
  {
    icon: Users,
    label_fr: "Aux membres",
    text_fr:
      "Vous avez le droit de comprendre chaque conclusion que nous produisons à votre sujet. Vous pouvez la contester, la corriger, l'exporter, la supprimer.",
  },
  {
    icon: GraduationCap,
    label_fr: "Aux chercheurs",
    text_fr:
      "La méthode IMES™ v2 est destinée à devenir un référentiel publiable — formules, pondérations, ontologie et biais reconnus documentés dans un livre blanc CC BY-NC.",
  },
  {
    icon: Building2,
    label_fr: "Aux institutions",
    text_fr:
      "Vous avez droit à un accès agrégé strictement anonymisé (k ≥ 20), à un dashboard territorial reproductible, et à un contrat d'audit d'implémentation sur demande.",
  },
];

const ROADMAP = [
  { label_fr: "Correctifs SEC-001 · SEC-002 · SEC-003", status: "done", date: "Févr. 2026" },
  { label_fr: "Doctrine IMES™ v2 ratifiée + Trusted Intelligence™ actif", status: "done", date: "Févr. 2026" },
  { label_fr: "Headers de sécurité renforcés (CSP · HSTS · Referrer-Policy)", status: "todo", date: "Q1 2026" },
  { label_fr: "Livre blanc ALTIORA Méthode™ · 40 pages FR/EN", status: "todo", date: "Q2 2026" },
  { label_fr: "Comité éthique externe (chercheur · praticien · juriste · citoyen)", status: "todo", date: "Q2 2026" },
  { label_fr: "Certification ISO 27001 pour usages institutionnels", status: "todo", date: "Q3 2026" },
];

export default function PublicSecurity() {
  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937]" data-testid="public-security">
      {/* Discreet top nav */}
      <nav className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="text-xs font-mono uppercase tracking-[0.32em] text-navy hover:text-[#7A5B14] transition">
          ALTIORA COOP
        </Link>
        <div className="flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-[#5B5B62]">
          <Link to="/" className="hover:text-navy transition">Accueil</Link>
          <Link to="/login" className="hover:text-navy transition">Se connecter</Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <motion.div {...fade(0)} className="max-w-4xl">
          <div className="inline-flex items-center gap-2 mb-6">
            <ShieldCheck className="h-4 w-4 text-[#7A5B14]" />
            <span className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
              Sécurité · Éthique · Explicabilité
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02] tracking-tight">
            Vos données, notre méthode,<br />
            <span className="text-[#7A5B14]">rien à cacher.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#374151] leading-relaxed max-w-3xl">
            ALTIORA construit un référentiel de diagnostic économico-social destiné à
            des membres individuels et à des partenaires institutionnels. Cette page
            décrit — sans marketing — ce que nous garantissons, ce que nous ne faisons
            pas, et ce qui reste à faire.
          </p>
          <div className="mt-10 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#5B5B62]">
            <RefreshCw className="h-3.5 w-3.5" /> Dernière mise à jour : 8 février 2026
          </div>
        </motion.div>
      </header>

      {/* Six pillars */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <motion.div {...fade(0)} className="mb-14 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Six garanties actives</div>
          <h2 className="font-display text-3xl md:text-4xl text-navy leading-tight">
            Ce qui est déjà en place, et vérifié.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="security-pillars">
          {PILLARS.map(({ icon: Icon, title_fr, body_fr, proof_fr }, i) => (
            <motion.div
              key={title_fr}
              {...fade(i * 0.06)}
              className="border-2 border-[#E4E1D8] bg-white rounded-xl p-6 md:p-7 hover:border-navy transition-colors"
              data-testid={`pillar-${i}`}
            >
              <Icon className="h-6 w-6 text-navy mb-4" />
              <h3 className="font-display text-xl text-navy leading-snug mb-3">{title_fr}</h3>
              <p className="text-sm text-[#374151] leading-relaxed mb-4">{body_fr}</p>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                <CheckCircle2 className="h-3 w-3" /> {proof_fr}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What we DON'T do */}
      <section className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <motion.div {...fade(0)} className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#C5A880] mb-3">Ce que nous ne faisons pas</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              L'IMES n'est pas un score de crédit.<br />
              Ce n'est pas un test psychométrique.<br />
              Ce n'est jamais un outil de sélection.
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
              Aucun tiers ne peut utiliser un IMES individuel pour trier des candidats,
              refuser un prêt ou catégoriser une personne. La méthode est conçue pour
              accompagner, pas pour classer. Le membre est toujours propriétaire de son
              résultat et peut le retirer à tout moment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Audiences */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <motion.div {...fade(0)} className="mb-14 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Vos droits</div>
          <h2 className="font-display text-3xl md:text-4xl text-navy leading-tight">
            Ce que nous vous devons, par écrit.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="security-audiences">
          {AUDIENCES.map(({ icon: Icon, label_fr, text_fr }, i) => (
            <motion.div
              key={label_fr}
              {...fade(i * 0.08)}
              className="border-l-2 border-[#7A5B14] pl-6 py-2"
            >
              <Icon className="h-5 w-5 text-[#7A5B14] mb-3" />
              <div className="font-display text-lg text-navy mb-3">{label_fr}</div>
              <p className="text-sm text-[#374151] leading-relaxed">{text_fr}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="bg-[#F1EFE9]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <motion.div {...fade(0)} className="mb-12 max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Feuille de route</div>
            <h2 className="font-display text-3xl md:text-4xl text-navy leading-tight">
              Ce qui reste à construire.
            </h2>
            <p className="mt-6 text-base text-[#374151] leading-relaxed max-w-2xl">
              La sécurité n'est jamais un état, c'est un chantier permanent.
              Voici notre calendrier public. Toute institution partenaire peut demander
              une revue trimestrielle de l'avancement.
            </p>
          </motion.div>
          <div className="space-y-3" data-testid="security-roadmap">
            {ROADMAP.map(({ label_fr, status, date }, i) => (
              <motion.div
                key={label_fr}
                {...fade(i * 0.04)}
                className={`flex items-center gap-4 p-4 rounded-lg border ${
                  status === "done"
                    ? "bg-white border-emerald-200"
                    : "bg-white border-[#E4E1D8]"
                }`}
                data-testid={`roadmap-${i}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-[#F1EFE9] text-[#8B8B8F]"
                }`}>
                  {status === "done" ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-mono">→</span>}
                </div>
                <div className="flex-1 text-sm text-[#374151]">{label_fr}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">{date}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact institutionnel */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <motion.div
          {...fade(0)}
          className="border-2 border-navy bg-white rounded-xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-8"
        >
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#7A5B14] mb-3">Contact institutionnel</div>
            <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight mb-4">
              Vous représentez un ministère, une université, un organisme communautaire ?
            </h2>
            <p className="text-base text-[#374151] leading-relaxed max-w-2xl">
              Nous ouvrons progressivement les licences institutionnelles. Une revue
              technique complète, un contrat d'audit d'implémentation et un dashboard
              anonymisé peuvent être discutés en amont d'un premier engagement.
            </p>
          </div>
          <Link
            to="/join/partenaire"
            data-testid="security-partner-cta"
            className="inline-flex items-center gap-3 px-7 py-4 bg-navy text-white rounded-full text-base font-medium hover:bg-[#123163] transition-all whitespace-nowrap"
          >
            Nous parler <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* Footer minimal */}
      <footer className="border-t border-[#E4E1D8] py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex items-center justify-between flex-wrap gap-4">
          <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#5B5B62]">
            © 2026 ALTIORA COOP · Tous droits réservés
          </div>
          <div className="flex items-center gap-6 text-xs text-[#5B5B62]">
            <Link to="/securite" className="hover:text-navy transition inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Sécurité
            </Link>
            <span className="inline-flex items-center gap-1.5 opacity-60">
              <Info className="h-3 w-3" /> Doctrine ALTIORA Méthode™ v2 ratifiée
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
